import React, { useState, useEffect } from 'react';
import { MicrophoneIcon } from '../icons';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../services/adminAPI';
import useLanguageOptions from '../hooks/useLanguageOptions';
import usePermissions from '../hooks/usePermissions';
import useDashboardTranscriptions from '../hooks/useDashboardTranscriptions';
import useRegistrarDashboardStats from '../hooks/useRegistrarDashboardStats';
import DuplicateSessionDialog from '../components/recording/DuplicateSessionDialog';
import RegistrarContextBar from '../components/dashboard/RegistrarContextBar';
import RegistrarStatsGrid from '../components/dashboard/RegistrarStatsGrid';
import RecentSessionsPanel from '../components/dashboard/RecentSessionsPanel';
import DashboardLoadingState from '../components/dashboard/DashboardLoadingState';
import DashboardTrustFooter from '../components/dashboard/DashboardTrustFooter';
import RecordingModal from '../components/dashboard/RecordingModal';
import UploadModal from '../components/dashboard/UploadModal';

const Dashboard = () => {
  const { user } = useAuth();
  const { languages } = useLanguageOptions();
  const { canRecord } = usePermissions();

  const [tenantActive, setTenantActive] = useState(true);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [duplicateSession, setDuplicateSession] = useState(null);

  const {
    transcriptions,
    loading,
    error,
    refetch,
    refreshTranscriptions,
    setLastUploadTime,
    addPendingSession,
  } = useDashboardTranscriptions();

  const { stats, recentSessions } = useRegistrarDashboardStats(transcriptions);

  const courtDisplayName = user?.court_name || user?.courtName || 'Court not assigned';
  const initiatorDisplayName = user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
    : 'Unknown user';

  useEffect(() => {
    const checkTenant = async () => {
      if (!user?.tenant_id) {
        setTenantActive(true);
        return;
      }
      try {
        const resp = await adminAPI.isTenantActive(user.tenant_id);
        setTenantActive(resp.data?.data?.active !== false);
      } catch {
        setTenantActive(true);
      }
    };
    checkTenant();
  }, [user?.tenant_id]);

  const handleUploadSuccess = (payload) => {
    setLastUploadTime(Date.now());
    if (payload && typeof payload === 'object') {
      addPendingSession(payload);
    }
    refreshTranscriptions();
  };

  if (loading) {
    return <DashboardLoadingState />;
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-6 items-stretch">
        <div className="lg:col-span-6 xl:col-span-5">
          <RegistrarContextBar
            user={user}
            error={error}
            onRetry={refetch}
            onStartRecording={() => setShowRecordingModal(true)}
            onStartUpload={() => setShowUploadModal(true)}
            canRecord={canRecord}
            tenantActive={tenantActive}
            compact
          />
        </div>
        <div className="lg:col-span-6 xl:col-span-7">
          <RegistrarStatsGrid stats={stats} />
        </div>
      </div>

      {!tenantActive && (
        <div className="mb-4 md-card md-card-body bg-matdash-danger-subtle border border-matdash-danger/20 text-sm text-red-800">
          Your court tenant has been deactivated. New recordings and uploads are blocked. Contact
          your platform administrator.
        </div>
      )}

      {duplicateSession && (
        <div className="mb-4">
          <DuplicateSessionDialog
            session={duplicateSession}
            onDismiss={() => setDuplicateSession(null)}
          />
        </div>
      )}

      <RecentSessionsPanel sessions={recentSessions} />

      <DashboardTrustFooter />

      {canRecord && tenantActive && (
        <button
          type="button"
          onClick={() => setShowRecordingModal(true)}
          className="dashboard-fab lg:hidden fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-matdash-primary text-white shadow-lg hover:bg-[#a04e08] transition-colors"
          aria-label="Start recording"
        >
          <MicrophoneIcon className="h-5 w-5" />
          <span className="text-sm font-medium">Record</span>
        </button>
      )}

      <RecordingModal
        open={showRecordingModal}
        onClose={() => setShowRecordingModal(false)}
        tenantId={user?.tenant_id}
        courtDisplayName={courtDisplayName}
        initiatorDisplayName={initiatorDisplayName}
        languages={languages}
        onUploadSuccess={handleUploadSuccess}
        onDuplicateSession={setDuplicateSession}
      />

      <UploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        tenantId={user?.tenant_id}
        courtDisplayName={courtDisplayName}
        initiatorDisplayName={initiatorDisplayName}
        languages={languages}
        onUploadSuccess={handleUploadSuccess}
        onDuplicateSession={setDuplicateSession}
      />
    </>
  );
};

export default Dashboard;
